import React, { useRef } from 'react';
import {
  AccountManager,
  ApiManager,
  BetslipProvider,
  Bus,
  createPreferencesState,
  GlobalBetslipProvider,
  logger,
  PreferencesProvider,
  SettingsManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useSettings,
} from '@apollo/core';
import Theme from 'themeSource/custom';
import { SportProvider } from '@apollo/core/src/state/sport/sport';
import { RacingProvider } from '@apollo/core/src/state/racing/racing';
import Loader from '../Loader/Loader';
import SQLiteStorageAdapter from '../../../core/Cordova/SQLiteStorageAdapter';
import ToastManager from '../../../core/ToastManager';
import Notifications from '../Notifications';
import Label from '../I18n/Label';
import { appEvents, cmsStaticBlock } from '../../../core/constants';
import useOnWidowResize from '../../../core/hooks/useOnWindowResize';
import useThemeBootstrap from '../../../core/hooks/useThemeBootstrap';
import CordovaProvider from '../../../core/Cordova/CordovaProvider';
import QueryRoutes from '../Layout/QueryRoutes';
import ToastContainer from '../Toast/ToastContainer';
import { SidebarProvider } from '../../../state/Sidebar/Sidebar';
import MobileNavFooter from '../Footer/MobileNavFooter';
import { BonusProvider } from '../../../state/Bonus/Bonus';
import StaticBlock from '../StaticBlock/StaticBlock';
import useTranslate from '../I18n/Interpreter';

const timeToHideLoader = Theme.TIME_TO_HIDE_LOADER ?? 0;

const GlobalLoading = ({ resolved, setLoading }) => (
  <main className='main main-loader'>
    {Theme.Loader ? (
      <Theme.Loader resolved={resolved} setLoading={setLoading} />
    ) : (
      <Loader className='empty-state' />
    )}
  </main>
);

const GlobalError = (
  <main className='main'>
    <div className='error-state'>
      <svg
        width='242'
        height='181'
        viewBox='0 0 242 181'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M48.1103 28.0102C48.1103 25.275 50.3276 23.0577 53.0628 23.0577H183.242C185.978 23.0577 188.195 25.275 188.195 28.0102C188.195 30.7454 185.978 32.9627 183.242 32.9627H53.0628C50.3276 32.9627 48.1103 30.7454 48.1103 28.0102ZM36.7898 32.9627C34.0546 32.9627 31.8373 35.18 31.8373 37.9152C31.8373 40.6503 34.0546 42.8676 36.7898 42.8676H166.969C169.705 42.8676 171.922 40.6503 171.922 37.9152C171.922 35.18 169.705 32.9627 166.969 32.9627H36.7898ZM198.807 42.8677C201.542 42.8677 203.759 45.085 203.759 47.8201C203.759 50.5553 201.542 52.7726 198.807 52.7726H182.535C185.27 52.7726 187.487 54.9899 187.487 57.7251C187.487 60.4603 185.27 62.6776 182.535 62.6776L176.167 62.6776C178.902 62.6776 181.119 64.8949 181.119 67.6301C181.119 70.3653 178.902 72.5826 176.167 72.5826H221.447C224.182 72.5826 226.399 74.7999 226.399 77.535C226.399 80.2702 224.182 82.4875 221.447 82.4875H237.012C239.747 82.4875 241.964 84.7048 241.964 87.44C241.964 90.1752 239.747 92.3925 237.012 92.3925H223.569C220.834 92.3925 218.617 94.6098 218.617 97.345C218.617 100.08 220.834 102.297 223.569 102.297H227.814C230.549 102.297 232.767 104.515 232.767 107.25C232.767 109.985 230.549 112.202 227.814 112.202H212.267C214.994 112.212 217.202 114.426 217.202 117.155C217.202 119.89 214.985 122.107 212.25 122.107H47.4026C44.6674 122.107 42.4501 119.89 42.4501 117.155C42.4501 114.426 44.6579 112.212 47.385 112.202H32.5449C29.8097 112.202 27.5924 109.985 27.5924 107.25C27.5924 104.515 29.8097 102.297 32.5449 102.297H4.95248C2.2173 102.297 0 100.08 0 97.345C0 94.6098 2.2173 92.3925 4.95248 92.3925H33.2524C35.9876 92.3925 38.2049 90.1752 38.2049 87.44C38.2049 84.7048 35.9876 82.4875 33.2524 82.4875H15.5649C12.8298 82.4875 10.6125 80.2702 10.6125 77.535C10.6125 74.7999 12.8298 72.5826 15.5649 72.5826H43.8649C41.1297 72.5826 38.9124 70.3653 38.9124 67.6301C38.9124 64.8949 41.1297 62.6776 43.8649 62.6776L52.3552 62.6776C49.62 62.6776 47.4027 60.4603 47.4027 57.7251C47.4027 54.9899 49.62 52.7726 52.3552 52.7726H68.6272C65.892 52.7726 63.6747 50.5553 63.6747 47.8201C63.6747 45.085 65.892 42.8677 68.6272 42.8677H198.807ZM188.919 112.202H191.007C190.649 112.201 190.3 112.162 189.963 112.088C189.627 112.162 189.278 112.201 188.919 112.202ZM68.6272 132.012C65.892 132.012 63.6747 129.795 63.6747 127.06C63.6747 124.325 65.892 122.107 68.6272 122.107H191.024C193.759 122.107 195.977 124.325 195.977 127.06C195.977 129.795 193.759 132.012 191.024 132.012H68.6272Z'
          fill='url(#paint0_linear)'
        />
        <path
          d='M126.28 54.8852L99.9736 32.8201L110.627 20.1465C114.97 14.9458 122.695 14.2894 127.845 18.6318L135.419 24.9938C140.62 29.3361 141.276 37.0614 136.934 42.2116L126.28 54.8852Z'
          fill='var(--dt-light-4)'
        />
        <path
          d='M96.2372 49.1796L96.0352 49.0281C95.0254 48.1697 94.8739 46.655 95.7323 45.6451L106.588 32.7191C107.446 31.7093 108.961 31.5578 109.971 32.4162L110.173 32.5676C111.183 33.426 111.334 34.9408 110.476 35.9506L99.6707 48.9271C98.8123 49.9369 97.2975 50.0379 96.2372 49.1796Z'
          fill='var(--dt-light-3)'
        />
        <path
          d='M110.981 61.5501L110.779 61.3987C109.77 60.5403 109.618 59.0255 110.476 58.0157L121.332 45.0897C122.191 44.0798 123.705 43.9284 124.715 44.7867L124.917 44.9382C125.927 45.7966 126.079 47.3113 125.22 48.3212L114.364 61.2472C113.506 62.257 111.991 62.4085 110.981 61.5501Z'
          fill='var(--dt-light-3)'
        />
        <path
          d='M127.138 53.4714L101.084 31.6082C100.378 31.0023 99.3172 31.1033 98.7618 31.8102L98.7113 31.8607C98.1054 32.5676 98.2064 33.6279 98.9133 34.1833L124.967 56.0465C125.674 56.6524 126.735 56.5514 127.29 55.8445L127.34 55.794C127.946 55.1376 127.845 54.0773 127.138 53.4714Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M126.633 35.4457L118.201 28.3767C117.949 28.1748 117.949 27.8213 118.151 27.5689C118.353 27.3164 118.706 27.3164 118.958 27.5184L127.391 34.5873C127.643 34.7893 127.643 35.1427 127.441 35.3952C127.189 35.6476 126.835 35.6476 126.633 35.4457Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M122.948 35.9001L118.404 32.0627C118.152 31.8607 118.152 31.5073 118.354 31.2548C118.556 31.0023 118.909 31.0023 119.162 31.2043L123.706 35.0417C123.958 35.2437 123.958 35.5971 123.756 35.8496C123.554 36.0516 123.201 36.1021 122.948 35.9001Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M120.322 36.9099L117.899 34.8398C117.697 34.6883 117.697 34.4358 117.848 34.2338L118 34.0319C118.151 33.8299 118.404 33.8299 118.606 33.9814L121.029 36.0516C121.231 36.203 121.231 36.4555 121.08 36.6575L120.928 36.8594C120.777 37.0109 120.474 37.0614 120.322 36.9099Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M212.47 16.5111C210.905 17.5714 208.936 18.3288 207.219 19.2882C205.704 18.8338 204.189 18.3793 202.675 17.8744C196.01 15.6023 188.89 12.5222 181.417 8.68482C169.552 2.57526 157.888 3.08019 146.78 10.0986C138.348 15.4508 133.349 22.8226 133.298 22.8731L131.632 21.7623L129.966 20.6515C129.966 20.6515 130.016 20.5505 130.067 20.4495C130.925 19.1872 136.025 12.1688 144.508 6.76612C153.041 1.31296 166.724 -3.43331 183.235 5.04938C193.889 10.553 203.634 14.3904 212.47 16.5111Z'
          fill='url(#paint1_linear)'
        />
        <path
          d='M196.264 165.564C195.658 165.867 195.203 166.271 194.85 166.776C191.719 168.089 188.387 169.704 185.004 171.472C167.079 181.015 152.235 182.933 140.924 177.177C130.27 171.775 125.524 161.02 123.555 154.456C122.646 151.376 122.293 149.205 122.242 148.902L126.231 148.346C126.231 148.397 126.484 150.113 127.241 152.689C128.958 158.546 133.199 168.745 142.742 173.643C152.84 178.743 166.372 176.874 183.085 167.988C186.216 166.321 189.296 164.807 192.275 163.544C193.133 164.908 194.699 165.918 196.264 165.564Z'
          fill='url(#paint2_linear)'
        />
        <path
          d='M96.8447 135.319L127.847 120.525L134.966 135.471C137.895 141.58 135.32 148.851 129.21 151.78L120.324 156.021C114.214 158.95 106.943 156.375 104.015 150.265L96.8447 135.319Z'
          fill='var(--dt-light-4)'
        />
        <path
          d='M97.753 136.632L128.452 121.989C129.26 121.585 129.614 120.576 129.26 119.768V119.717C128.856 118.909 127.846 118.556 127.038 118.909L96.3392 133.552C95.5313 133.956 95.1779 134.966 95.5313 135.774V135.824C95.8848 136.683 96.8946 137.036 97.753 136.632Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M113.76 143.651L123.707 138.904C124.009 138.753 124.312 138.904 124.464 139.157C124.615 139.46 124.464 139.763 124.211 139.914L114.264 144.66C113.962 144.812 113.659 144.66 113.507 144.408C113.356 144.105 113.457 143.802 113.76 143.651Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M112.246 140.469L122.193 135.723C122.496 135.572 122.799 135.723 122.95 135.976C123.102 136.279 122.95 136.582 122.698 136.733L112.751 141.479C112.448 141.631 112.145 141.479 111.993 141.227C111.842 140.924 111.943 140.57 112.246 140.469Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M110.68 137.288L120.627 132.542C120.93 132.391 121.233 132.542 121.384 132.795C121.536 133.098 121.384 133.401 121.132 133.552L111.185 138.298C110.882 138.45 110.579 138.298 110.427 138.046C110.326 137.743 110.427 137.389 110.68 137.288Z'
          fill='var(--dt-light-1)'
        />
        <path
          d='M48.5732 96.4403C55.2887 100.581 62.4586 103.863 69.9819 106.438C76.1925 108.558 82.504 110.124 89.0175 111.033C96.6418 112.143 104.317 112.446 112.042 111.84C123.049 111.033 133.703 108.558 143.953 104.368C154.405 100.126 163.897 94.3196 172.38 86.8972C172.835 86.4933 173.289 86.1398 173.743 85.7359C155.667 99.7222 135.268 106.791 112.496 106.943C93.8143 107.044 77.5053 101.641 66.3465 95.5314C86.8463 105.226 108.255 108.154 130.472 103.509C152.789 98.8134 171.219 87.6041 185.71 69.9823C185.458 70.1843 185.256 70.3358 185.003 70.5378C175.713 78.1621 165.21 83.4638 153.547 86.3418C147.134 87.9071 140.671 88.614 134.107 88.5635C126.634 88.4625 119.363 87.3012 112.244 85.029C103.357 82.2015 95.2785 77.8591 88.0077 71.9515C87.5027 71.5476 87.5027 71.5476 87.7552 70.9417C105.932 84.2211 127.644 86.8972 144.105 83.8172C143.65 83.8172 143.145 83.8677 142.691 83.8677C142.236 83.8677 141.731 83.8677 141.277 83.9182C134.511 84.0192 127.846 83.1608 121.333 81.2926C112.496 78.8185 104.519 74.7286 97.3487 68.9725C93.4608 65.8925 89.9769 62.4085 86.9473 58.5206C86.7454 58.2681 86.5939 57.9652 86.4929 57.6622C86.3414 57.2583 86.1899 56.9049 86.0385 56.5009C85.685 56.9049 85.3316 57.3088 84.9781 57.6622C85.3821 58.8236 85.685 59.9849 85.9375 61.1967C86.2404 62.56 86.6444 63.9738 86.1394 65.4885C85.988 65.1856 85.8365 64.9331 85.685 64.6807C85.7355 65.0341 85.7355 65.3875 85.685 65.6905C85.9375 66.1954 86.089 66.8013 85.988 67.4577C85.786 69.225 85.786 71.0427 85.483 72.7594C85.1801 74.3752 84.8266 76.0919 84.0693 77.5562C83.7158 78.2126 83.3119 78.8185 82.8575 79.4244C82.8575 79.5254 82.9079 79.6263 82.9079 79.7273C82.9079 79.8788 82.8575 80.0808 82.807 80.2323C83.0594 79.9293 83.3119 79.6768 83.6148 79.3739C80.7873 85.231 76.4449 89.6238 70.4364 92.4009C70.4869 92.3504 70.5374 92.2494 70.5374 92.1989C70.3354 92.2999 70.0829 92.4009 69.881 92.5019C69.7295 92.7543 69.5275 93.0068 69.1236 93.1583C64.7812 94.9255 60.3379 95.5819 55.7936 95.178C55.3392 95.2284 54.8343 95.3294 54.3798 95.3799C52.3602 95.6324 50.391 95.8343 48.5732 96.4403Z'
          fill='var(--dt-light-2)'
        />
        <path
          d='M80.5443 62.4808C80.8199 63.4362 81.0702 64.4202 81.2953 65.4328C81.5203 66.4454 81.6806 67.5026 81.7762 68.6043C81.9518 71.8622 81.5632 74.9527 80.6103 77.8756C79.6541 80.7447 78.1934 83.321 76.2282 85.6044C74.4078 87.6088 72.3183 89.1976 69.9599 90.3708C67.6267 91.5155 65.1288 92.1842 62.4663 92.3768C59.6389 92.5257 56.9395 92.1262 54.3681 91.1785C51.7967 90.2308 49.4626 88.7551 47.3659 86.7513C45.2691 84.7474 43.6257 82.4318 42.4357 79.8042C41.2423 77.1229 40.5502 74.2482 40.3594 71.1803C40.3177 70.0753 40.3458 69.0064 40.4436 67.9737C40.5381 66.8872 40.6629 65.8529 40.8179 64.8707C41.1718 62.7414 41.2809 60.803 41.1451 59.0555C40.9808 57.2826 40.1816 55.5088 38.7475 53.7339C38.1469 54.0685 37.5127 54.297 36.845 54.4196C35.0361 57.0445 33.6889 59.9245 32.8032 63.0593C31.9142 66.1404 31.5752 69.3763 31.7861 72.7672C32.4917 81.0711 35.7745 87.8099 41.6345 92.9836C47.4945 98.1573 54.6024 100.592 62.9584 100.289C71.2623 99.5832 78.0012 96.3004 83.1749 90.4404C88.32 84.5552 90.7551 77.4472 90.48 69.1165C90.2406 65.7004 89.4888 62.5188 88.2249 59.5717C86.9576 56.5707 85.2639 53.8799 83.1437 51.4994C82.6559 51.4757 82.1774 51.3839 81.7082 51.2239C80.236 53.1796 79.5138 55.0345 79.5419 56.7888C79.57 58.5431 79.9041 60.4404 80.5443 62.4808ZM45.7686 70.8439C46.1809 75.7351 47.9121 79.6797 50.9622 82.6778C54.0124 85.676 57.7349 87.1059 62.1298 86.9676C64.0675 86.8471 65.8883 86.3691 67.5924 85.5337C69.3217 84.6697 70.8425 83.4945 72.1548 82.0081C73.6691 80.293 74.7899 78.3322 75.5171 76.1257C76.241 73.8654 76.5243 71.4704 76.367 68.9407C76.1841 67.3041 75.8365 65.6238 75.324 63.8996C74.5527 61.4892 74.1767 58.9191 74.1961 56.1893C74.1868 53.4343 75.4214 50.4803 77.8998 47.3274C77.7788 47.1188 77.6973 46.8943 77.6553 46.6537C75.0303 44.8449 72.1647 43.5102 69.0584 42.6498C65.9488 41.7356 62.686 41.3982 59.2699 41.6377C55.879 41.8486 52.6974 42.6003 49.7251 43.8929C46.7493 45.1315 44.0712 46.811 41.6906 48.9312L41.8621 49.0826C44.6112 52.0184 46.1266 54.8824 46.4083 57.6745C46.7153 60.438 46.6355 63.0635 46.1688 65.5509C45.8521 67.4077 45.7186 69.172 45.7686 70.8439Z'
          fill='var(--dt-light-3)'
        />
        <defs>
          <linearGradient
            id='paint0_linear'
            x1='84.1922'
            y1='77.535'
            x2='246.209'
            y2='9.26152'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='var(--dt-light-1)' />
            <stop offset='1' stopColor='var(--dt-light-1)' stopOpacity='0' />
          </linearGradient>
          <linearGradient
            id='paint1_linear'
            x1='147.159'
            y1='11.7378'
            x2='202.343'
            y2='11.7378'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='var(--dt-light-2)' />
            <stop offset='1' stopColor='var(--dt-light-2)' stopOpacity='0' />
          </linearGradient>
          <linearGradient
            id='paint2_linear'
            x1='137.667'
            y1='164.514'
            x2='187.178'
            y2='164.514'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='var(--dt-light-2)' />
            <stop offset='1' stopColor='var(--dt-light-2)' stopOpacity='0' />
          </linearGradient>
        </defs>
      </svg>

      <h1 className='title'>We&apos;ll be right back!</h1>

      <span>
        Sorry, but the site is temporarily unavailable. We already working to restore it! Please try
        again later
      </span>
    </div>
  </main>
);

const AuthorizationLoader = (props) => {
  const t = useTranslate();
  const { resolved } = useApplicationState();
  const { clientBlocked, country = null, geoError = null } = useSettings();
  const { children } = props;

  if (clientBlocked) {
    return (
      <div className='global-loader global-loader--blocked'>
        <Loader iconName='warning' />
        <div className='content'>
          <h1 className='content__title'>
            <Label message='blocked' />
          </h1>
          <div className='content__body'>
            {country && (
              <h3 className='content__body-title'>
                {t('geo_block_detected_country', { country })}
              </h3>
            )}
            {geoError && (
              <h3 className='content__body-title'>
                {`Error: ${geoError.message} (${geoError.code})`}
              </h3>
            )}
            <StaticBlock options={{ source: cmsStaticBlock.ClientBlocked }} />
          </div>
        </div>
      </div>
    );
  }

  if (!resolved) {
    return (
      <div className='global-loader'>
        <Loader />
        <Label message='authenticating' />
      </div>
    );
  }
  return children;
};

// ignore click on these elements.
const menuClasses = {
  mainMenu: '.header__menu--active, .navbar-toggler',
  sideBarLeft: '.main__sidebar--left, .navbar-toggler',
  sideBarRight: '.main__sidebar--right, .btn-betslip-show',
};

const menuEvents = {
  mainMenu: Bus.events.layout.setMainMenu,
  sideBarLeft: Bus.events.layout.setSideBarLeft,
  sideBarRight: Bus.events.layout.setSideBarRight,
};

const RootLayout = (props) => {
  useThemeBootstrap();
  const { children } = props;
  const favoritesState = createPreferencesState();
  const [loading, setLoading] = React.useState(true);
  const [resolveStatus, _setResolveStatus] = React.useState(undefined);
  const { resolved, layout } = useApplicationState();
  const dimensions = useOnWidowResize();

  const resolveStatusRef = React.useRef(resolveStatus);
  const setResolveStatus = (data) => {
    resolveStatusRef.current = data;
    _setResolveStatus(data);
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      logger.debug('Device pause.', resolveStatusRef.current);
      Bus.emit(appEvents.devicePause);
    } else {
      logger.debug('Device resume.', resolveStatusRef.current);
      Bus.emit(appEvents.deviceResume);
      if (!resolveStatusRef.current) {
      }
    }
  };

  const onDeviceOffline = () => {
    logger.debug('Device offline.', resolveStatusRef.current);
    ToastManager.error(<Label message='lost_internet_connection' />);
    Bus.emit(appEvents.deviceNetworkOffline);
  };

  const onDeviceOnline = () => {
    logger.debug('Device online.', resolveStatusRef.current);
    if (!resolveStatusRef.current) {
      return;
    }
    ToastManager.success(<Label message='recovered_internet_connection' />);
    Bus.emit(appEvents.deviceNetworkOnline);
  };

  const onDeviceReady = async () => {
    document.removeEventListener('deviceready', onDeviceReady, false);

    if (CORDOVA) {
      Bus.emit(appEvents.cordovaDeviceReady);

      // Would be better to have this in the Cordova component,
      // but Akira relies on the StorageAdapter as soon as available
      StorageManager.setStorageAdapter(new SQLiteStorageAdapter());
    }

    await AccountManager.getToken().then((token) => {
      ApiManager.setAuthorizationToken(token);
    });

    await AccountManager.resolveAuthentication().catch((error) => {
      // Logging auth resolve error
      logger.debug('[BOOT UP]', error.message);
    });

    Promise.all([
      await SiteConfigManager.resolveCmsConfig(),
      await SettingsManager.resolveSettings(),
    ])
      .then(() => {
        logger.debug('zzz - application is ready.');
        setResolveStatus(true);
        setTimeout(() => {
          setLoading(false);
        }, timeToHideLoader);
      })
      .catch((error) => {
        // console.log('zzz - application is NOT ready.');
        logger.error('[GlobalSettingsLoader]', 'unable to resolve initial settings', error);
        setResolveStatus(false);
        setLoading(false);
      });
  };

  const resolveDeviceMode = (layout, dimensions) => {
    if (dimensions.width < 768) {
      // Mobile
      Bus.send({
        event: Bus.events.layout.setLayout,
        data: {
          ...layout,
          sideBarLeft: false,
          sideBarRight: false,
          mobileDevice: true,
          tabletDevice: false,
          desktopDevice: false,
        },
      });
    } else if (dimensions.width < 1200) {
      // Tablet
      Bus.send({
        event: Bus.events.layout.setLayout,
        data: {
          ...layout,
          sideBarLeft: false,
          sideBarRight: false,
          mobileDevice: false,
          tabletDevice: true,
          desktopDevice: false,
        },
      });
    } else {
      // Desktop
      Bus.send({
        event: Bus.events.layout.setLayout,
        data: {
          ...layout,
          sideBarLeft: true,
          sideBarRight: true,
          mobileDevice: false,
          tabletDevice: false,
          desktopDevice: true,
        },
      });
    }
  };

  const closeMenu = React.useCallback(
    (e) => {
      Object.entries(menuClasses).forEach(([menuType, menuClass]) => {
        if (
          layout.mobileDevice
          && layout[menuType]
          && !e.target.closest(menuClass)
          && !e.target.closest('.modal')
        ) {
          // it only for mobiles and outside controls and not on a modal
          Bus.send({
            event: menuEvents[menuType],
            data: false,
          });
        }
      });

      if (
        layout.tabletDevice
        && !e.target.closest(menuClasses.sideBarLeft)
        && !e.target.closest('.modal')
      ) {
        Bus.send({
          event: menuEvents.sideBarLeft,
          data: false,
        });
      }
    },
    [layout],
  );

  React.useEffect(() => {
    if (CORDOVA) {
      document.addEventListener('deviceready', onDeviceReady, false);
    } else {
      onDeviceReady();
      window.addEventListener('offline', onDeviceOffline, false);
      window.addEventListener('online', onDeviceOnline, false);
      document.addEventListener('visibilitychange', onVisibilityChange, false);
    }
  }, []);

  const timeoutRef = useRef(null);
  // Watch for screen dimension changes
  React.useEffect(() => {
    // Debounce
    timeoutRef.current = setTimeout(() => {
      resolveDeviceMode(layout, dimensions);
    }, 200);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [resolved, dimensions.width]);

  React.useEffect(() => {
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [closeMenu]);

  if (loading || !resolveStatus) {
    return <GlobalLoading resolved={resolveStatus} setLoading={setLoading} />;
  }

  if (!resolveStatus) {
    return GlobalError;
  }

  return (
    <SidebarProvider>
      <CordovaProvider>
        <AuthorizationLoader>
          <PreferencesProvider value={favoritesState}>
            <GlobalBetslipProvider>
              <BonusProvider>
                <BetslipProvider>
                  <SportProvider>
                    <RacingProvider>
                      {children}
                      <MobileNavFooter />
                      <QueryRoutes />
                      <Notifications />
                      <ToastContainer />
                    </RacingProvider>
                  </SportProvider>
                </BetslipProvider>
              </BonusProvider>
            </GlobalBetslipProvider>
          </PreferencesProvider>
        </AuthorizationLoader>
      </CordovaProvider>
    </SidebarProvider>
  );
};

export default RootLayout;
